/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import React, { useRef, useState } from 'react';
import { Typography } from '@material-ui/core';
import { Field, FieldProps } from 'formik';
import { FormTextField, TextFieldPopover, Popover } from 'components/TextField';

import { moneyMask } from 'utils/masks';
import useFieldCustomization from 'hooks/useFieldCustomization';
import useTranslate, { Trans } from 'hooks/useTranslate';
import withOptional from './withOptional';

const styles = {
  minimumAmountPopup: () =>
    css`
      margin-bottom: 1em;
    `,
};

interface PropTypes {
  fieldId?: string;
  fieldName?: string;
  label?: string;
}

const AnnualIncomeField: React.FC<PropTypes> = ({
  fieldId,
  fieldName,
  label,
}) => {
  const annualIncomeRef = useRef<HTMLDivElement | null>(null);
  const { isRequired, isVisible } = useFieldCustomization();
  const [showSub10kIncomeDisclosure, setShowSub10kIncomeDisclosure] = useState<
    boolean
  >(false);
  const t = useTranslate();

  if (!isVisible('annualIncome')) {
    return null;
  }

  return (
    <React.Fragment>
      <Field name={fieldName || 'income'}>
        {(fieldProps: FieldProps) => (
          <FormTextField
            id={fieldId || 'creditAppIncome'}
            inputRef={annualIncomeRef}
            fieldProps={fieldProps}
            required={isRequired('annualIncome')}
            label={
              label ||
              withOptional(
                t,
                'formLabel.annualIncome',
                isRequired('annualIncome'),
              )
            }
            mask={moneyMask}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            // Evaluate the entered income on blur to give the user a chance to
            // fully enter their income before we maybe display the "sub 10k"
            // disclosure.
            onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
              const rawValue = event.target.value;
              const annualIncomeInput = Number(
                rawValue.replace(/[^0-9.-]+/g, ''),
              );

              // Don't show disclosure if the current value is the empty string.
              if (rawValue.length > 0 && annualIncomeInput < 10000) {
                setShowSub10kIncomeDisclosure(true);
              } else {
                setShowSub10kIncomeDisclosure(false);
              }
            }}
          />
        )}
      </Field>

      {t('creditApplication.insufficientIncomeDisclosure') === '' ? null : (
        <div css={styles.minimumAmountPopup}>
          <Popover
            open={showSub10kIncomeDisclosure}
            popperId={'insufficientIncomeDisclosurePopup'}
            width={
              annualIncomeRef.current
                ? annualIncomeRef.current.offsetWidth + 4
                : 0
            }
          >
            <Typography variant="body2" gutterBottom={false}>
              <Trans i18nKey="creditApplication.insufficientIncomeDisclosure" />
            </Typography>
          </Popover>
        </div>
      )}

      <TextFieldPopover id="annualIncome" textFieldRef={annualIncomeRef}>
        <Typography variant="body2" gutterBottom={true}>
          <Trans i18nKey="creditApplication.incomeDisclosure1" />
        </Typography>

        <Typography variant="body2" gutterBottom={false}>
          <Trans i18nKey="creditApplication.incomeDisclosure2" />
        </Typography>
      </TextFieldPopover>
    </React.Fragment>
  );
};

export default AnnualIncomeField;
